import { Children, isValidElement, useEffect, useRef, useState } from 'react';
import { Calendar, Clock, List, Link2, Printer, ChevronDown, ChevronUp } from 'lucide-react';
import styles from '@/styles/prose.module.css';

/**
 * Layout for the two long legal documents. See docs/redesign/legal-mockup.html.
 *
 * The table of contents is derived from the LegalSection children rather than
 * declared separately, so it cannot drift from the headings it points at, and
 * it renders in the initial markup — both routes are prerendered, so a TOC
 * built in an effect would be missing from the snapshot.
 *
 * Scroll-spy, reading progress and back-to-top are enhancements layered on in
 * an effect. Nothing here starts hidden and reveals via JS.
 */

export const slugify = (title) =>
  'section-' + title.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

export const LegalSection = ({ title, children }) => (
  <section className={styles.section} id={slugify(title)}>
    <div className={styles.sectionHead}>
      <h2>{title}</h2>
      <a className={styles.anchor} href={`#${slugify(title)}`} aria-label={`Link to ${title}`}>
        #
      </a>
    </div>
    {children}
  </section>
);

const LegalDoc = ({ title, updated, isoDate, readingTime, testId, children }) => {
  const sections = Children.toArray(children)
    .filter((child) => isValidElement(child) && child.props?.title)
    .map((child) => child.props.title);

  const [active, setActive] = useState(sections[0] ? slugify(sections[0]) : '');
  const [progress, setProgress] = useState(0);
  const [showTop, setShowTop] = useState(false);
  const [copied, setCopied] = useState(false);
  const copyTimer = useRef(null);

  useEffect(() => {
    const ids = sections.map(slugify);

    const onScroll = () => {
      const doc = document.documentElement;
      const line = 140;
      let current = ids[0];
      ids.forEach((id) => {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= line) current = id;
      });
      // Near the bottom the remaining sections can be too short to ever cross
      // the line, so the last one could never become current however far you
      // scrolled. Whatever is on screen at the end is what is being read.
      if (doc.scrollTop + doc.clientHeight >= doc.scrollHeight - 4) {
        current = ids[ids.length - 1];
      }
      setActive(current);

      const max = doc.scrollHeight - doc.clientHeight;
      setProgress(max > 0 ? Math.min(100, (doc.scrollTop / max) * 100) : 0);
      setShowTop(doc.scrollTop > 600);
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
    // sections is derived from children, which are static per page
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => clearTimeout(copyTimer.current), []);

  const copyLink = () => {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      copyTimer.current = setTimeout(() => setCopied(false), 1600);
    });
  };

  const tocLinks = sections.map((section) => {
    const id = slugify(section);
    return (
      <li key={id}>
        <a href={`#${id}`} aria-current={active === id ? 'true' : undefined}>
          {section}
        </a>
      </li>
    );
  });

  return (
    <div className={styles.page} data-testid={testId}>
      <div className={styles.progressTrack} aria-hidden="true">
        <div className={styles.progressFill} style={{ width: `${progress}%` }} />
      </div>

      <div className={styles.docHead}>
        <div className={styles.docHeadInner}>
          <span className={styles.eyebrow}>Legal</span>
          <h1 className={styles.title}>{title}</h1>

          <div className={styles.meta}>
            <div>
              <Calendar size={15} aria-hidden="true" />
              Last updated <time dateTime={isoDate}>{updated}</time>
            </div>
            <div>
              <Clock size={15} aria-hidden="true" />
              {readingTime}
            </div>
            <div>
              <List size={15} aria-hidden="true" />
              {sections.length} sections
            </div>
          </div>

          <div className={styles.docActions}>
            <button
              type="button"
              className={styles.docBtn}
              onClick={() => window.print()}
              data-testid="print-document"
            >
              <Printer size={16} aria-hidden="true" /> Print or save as PDF
            </button>
            <button
              type="button"
              className={styles.docBtn}
              onClick={copyLink}
              data-testid="copy-document-link"
            >
              <Link2 size={16} aria-hidden="true" /> {copied ? 'Copied' : 'Copy link'}
            </button>
          </div>
        </div>
      </div>

      <div className={styles.layout}>
        <aside className={styles.toc} aria-label="On this page">
          <p className={styles.tocTitle}>On this page</p>
          {/* On a phone the rail becomes a disclosure rather than disappearing —
              the need to skip to a clause is greater there, not smaller. */}
          <details className={styles.tocJump} data-testid="toc-jump">
            <summary>
              Jump to section
              <ChevronDown size={16} aria-hidden="true" />
            </summary>
            <ul className={styles.tocList}>{tocLinks}</ul>
          </details>
          <ul className={`${styles.tocList} ${styles.tocRail}`}>{tocLinks}</ul>
        </aside>

        <article className={styles.doc} data-testid="legal-document">
          {children}
        </article>
      </div>

      <button
        type="button"
        className={`${styles.toTop} ${showTop ? styles.toTopShow : ''}`}
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        aria-label="Back to top"
        data-testid="back-to-top"
      >
        <ChevronUp size={18} aria-hidden="true" />
      </button>
    </div>
  );
};

export default LegalDoc;
